import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Send, Volume2, VolumeX, StopCircle, Shield, AlertTriangle, Clock, Loader2, CheckCircle, Bot, User } from 'lucide-react';
import api from '@/lib/api';

export default function ScreeningPage() {
  const { callId } = useParams();
  const navigate = useNavigate();
  const [call, setCall] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [audioEnabled, setAudioEnabled] = useState(true);
  const [elapsed, setElapsed] = useState(0);
  const [proctoring, setProctoring] = useState({ score: 100, events: [] });
  const [ended, setEnded] = useState(false);
  const chatEndRef = useRef(null);
  const audioRef = useRef(null);
  const startTimeRef = useRef(Date.now());

  useEffect(() => {
    const fetchCall = async () => {
      try {
        const { data } = await api.get(`/calls/${callId}`);
        setCall(data);
        if (data.status === 'completed') setEnded(true);
      } catch (err) { navigate('/calls'); }
    };
    fetchCall();
  }, [callId, navigate]);

  useEffect(() => {
    if (ended) return;
    const timer = setInterval(() => setElapsed(Math.floor((Date.now() - startTimeRef.current) / 1000)), 1000);
    return () => clearInterval(timer);
  }, [ended]);

  useEffect(() => { chatEndRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages]);

  const logProctoringEvent = useCallback(async (type) => {
    try {
      const { data } = await api.post(`/calls/${callId}/proctoring`, { type });
      setProctoring(prev => ({ score: data.proctoring_score, events: [...prev.events, { type, time: new Date().toLocaleTimeString() }] }));
    } catch (err) { logger.error(err); }
  }, [callId]);

  useEffect(() => {
    const handleVisibility = () => { if (document.hidden && !ended) logProctoringEvent('tab_switch'); };
    const handleCopy = () => { if (!ended) logProctoringEvent('copy_detected'); };
    const handlePaste = () => { if (!ended) logProctoringEvent('paste_detected'); };
    document.addEventListener('visibilitychange', handleVisibility);
    document.addEventListener('copy', handleCopy);
    document.addEventListener('paste', handlePaste);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibility);
      document.removeEventListener('copy', handleCopy);
      document.removeEventListener('paste', handlePaste);
    };
  }, [ended, logProctoringEvent]);

  useEffect(() => {
    if (call && messages.length === 0 && !ended) {
      sendMessage('Hello, I am ready for the screening.', true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [call]);

  const sendMessage = async (text, isInitial = false) => {
    if (!text.trim() || sending || ended) return;
    const userMsg = { role: 'user', content: text, time: new Date().toLocaleTimeString() };
    if (!isInitial) setMessages(prev => [...prev, userMsg]);
    else setMessages([userMsg]);
    setInput('');
    setSending(true);
    try {
      const { data } = await api.post('/voice/chat', {
        message: text,
        agent_id: call?.agent_id,
        session_id: callId,
        candidate_id: call?.candidate_id,
        job_id: call?.job_id,
        include_audio: audioEnabled
      });
      const aiMsg = { role: 'assistant', content: data.response, time: new Date().toLocaleTimeString() };
      setMessages(prev => [...prev, aiMsg]);
      if (data.audio_base64 && audioEnabled) {
        try {
          const audio = new Audio(`data:audio/mp3;base64,${data.audio_base64}`);
          audioRef.current = audio;
          audio.play().catch(() => {});
        } catch (e) { logger.warn('Audio playback failed'); }
      }
    } catch (err) {
      setMessages(prev => [...prev, { role: 'system', content: 'Failed to get response. Please try again.', time: new Date().toLocaleTimeString() }]);
    } finally { setSending(false); }
  };

  const endScreening = async () => {
    setEnded(true);
    if (audioRef.current) audioRef.current.pause();
    const transcript = messages.map(m => `${m.role === 'assistant' ? 'AI' : 'Candidate'}: ${m.content}`).join('\n');
    try {
      await api.patch(`/calls/${callId}`, { status: 'completed', duration_seconds: elapsed, transcript, ended_at: new Date().toISOString() });
    } catch (err) { logger.error(err); }
  };

  const formatTime = (secs) => {
    const m = Math.floor(secs / 60).toString().padStart(2, '0');
    const s = (secs % 60).toString().padStart(2, '0');
    return `${m}:${s}`;
  };

  if (!call) return <div className="p-8 bg-white animate-pulse"><div className="h-8 bg-slate-100 rounded w-48" /></div>;

  return (
    <div className="h-full flex flex-col bg-white" data-testid="screening-page" style={{ fontFamily: 'Georgia, serif' }}>
      {/* Header */}
      <div className="flex items-center justify-between px-6 py-4 bg-white border-b border-slate-200 shadow-sm">
        <div className="flex items-center gap-4">
          <button
            data-testid="back-from-screening"
            onClick={() => navigate('/calls')}
            className="p-2 text-slate-400 hover:text-slate-700 hover:bg-slate-50 rounded-lg transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <h2 className="text-sm font-semibold text-slate-800">AI Screening Session</h2>
            <p className="text-xs text-slate-400 font-mono">{callId.slice(0, 8)}...</p>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2 px-3 py-1.5 bg-slate-50 border border-slate-200 rounded-lg">
            <Clock className="w-3.5 h-3.5 text-slate-400" />
            <span className="text-sm font-semibold text-slate-700 font-mono" data-testid="screening-timer">{formatTime(elapsed)}</span>
          </div>

          <div className={`flex items-center gap-2 px-3 py-1.5 rounded-lg border ${
            proctoring.score >= 80 ? 'bg-emerald-50 border-emerald-200' :
            proctoring.score >= 50 ? 'bg-amber-50 border-amber-200' : 'bg-red-50 border-red-200'
          }`}>
            <Shield className={`w-3.5 h-3.5 ${proctoring.score >= 80 ? 'text-emerald-600' : proctoring.score >= 50 ? 'text-amber-600' : 'text-red-500'}`} />
            <span className={`text-sm font-semibold font-mono ${proctoring.score >= 80 ? 'text-emerald-700' : proctoring.score >= 50 ? 'text-amber-700' : 'text-red-600'}`} data-testid="proctoring-score">
              {proctoring.score}%
            </span>
          </div>

          <button
            data-testid="toggle-audio-btn"
            onClick={() => setAudioEnabled(!audioEnabled)}
            className={`p-2 rounded-lg border transition-colors ${audioEnabled ? 'border-[#2563eb]/30 text-[#2563eb] bg-blue-50' : 'border-slate-200 text-slate-400'}`}
          >
            {audioEnabled ? <Volume2 className="w-4 h-4" /> : <VolumeX className="w-4 h-4" />}
          </button>

          {!ended && (
            <button
              data-testid="end-screening-btn"
              onClick={endScreening}
              className="flex items-center gap-2 bg-red-500 hover:bg-red-600 text-white text-sm font-semibold px-4 py-2 rounded-xl transition-colors shadow-sm"
            >
              <StopCircle className="w-4 h-4" /> End Session
            </button>
          )}
        </div>
      </div>

      <div className="flex-1 flex overflow-hidden">
        {/* Chat area */}
        <div className="flex-1 flex flex-col">
          <div className="flex-1 overflow-auto p-6 space-y-4 bg-slate-50" data-testid="chat-messages">
            {messages.map((msg, i) => (
              <div key={msg.id || `${msg.role}-${i}`} className={`flex gap-3 ${msg.role === 'user' ? 'flex-row-reverse' : ''}`}>
                <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                  msg.role === 'assistant' ? 'bg-[#2563eb] text-white' :
                  msg.role === 'system' ? 'bg-red-100 text-red-500' :
                  'bg-slate-200 text-slate-600'
                }`}>
                  {msg.role === 'assistant' ? <Bot className="w-4 h-4" /> :
                   msg.role === 'system' ? <AlertTriangle className="w-4 h-4" /> :
                   <User className="w-4 h-4" />}
                </div>
                <div className={`max-w-[70%] rounded-2xl px-4 py-3 shadow-sm ${
                  msg.role === 'assistant' ? 'bg-white border border-slate-200 text-slate-700' :
                  msg.role === 'system' ? 'bg-red-50 border border-red-200 text-red-600' :
                  'bg-[#2563eb] text-white'
                }`}>
                  <p className="text-sm leading-relaxed">{msg.content}</p>
                  <p className={`text-[10px] mt-1.5 font-mono ${msg.role === 'user' ? 'text-white/60' : 'text-slate-400'}`}>{msg.time}</p>
                </div>
              </div>
            ))}
            {sending && (
              <div className="flex gap-3">
                <div className="w-8 h-8 rounded-full bg-[#2563eb] flex items-center justify-center">
                  <Bot className="w-4 h-4 text-white" />
                </div>
                <div className="bg-white border border-slate-200 rounded-2xl px-4 py-3">
                  <Loader2 className="w-4 h-4 text-[#2563eb] animate-spin" />
                </div>
              </div>
            )}
            <div ref={chatEndRef} />
          </div>

          {!ended ? (
            <div className="p-4 border-t border-slate-200 bg-white">
              <form
                onSubmit={(e) => { e.preventDefault(); sendMessage(input); }}
                className="flex gap-3"
                data-testid="chat-input-form"
              >
                <input
                  data-testid="chat-input"
                  value={input}
                  onChange={e => setInput(e.target.value)}
                  placeholder="Type your response to the interviewer..."
                  className="flex-1 bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 text-slate-800 text-sm placeholder-slate-400 focus:border-[#2563eb] focus:ring-2 focus:ring-[#2563eb]/20 outline-none"
                  disabled={sending}
                />
                <button
                  data-testid="send-message-btn"
                  type="submit"
                  disabled={!input.trim() || sending}
                  className="bg-[#2563eb] hover:bg-[#1d4ed8] text-white px-5 rounded-xl transition-colors disabled:opacity-50 shadow-sm"
                >
                  <Send className="w-4 h-4" />
                </button>
              </form>
            </div>
          ) : (
            <div className="p-4 border-t border-slate-200 bg-emerald-50 text-center">
              <div className="flex items-center justify-center gap-2 text-emerald-700">
                <CheckCircle className="w-5 h-5" />
                <p className="text-sm font-semibold">Screening completed</p>
              </div>
              <p className="text-slate-500 text-xs mt-1">Duration: {formatTime(elapsed)} · Proctoring Score: {proctoring.score}%</p>
            </div>
          )}
        </div>

        {/* Proctoring panel */}
        <div className="w-[240px] border-l border-slate-200 bg-white p-5 overflow-auto hidden lg:block" data-testid="proctoring-panel">
          <h3 className="text-sm font-semibold text-slate-700 mb-4 flex items-center gap-2">
            <Shield className="w-4 h-4 text-[#2563eb]" /> Proctoring Monitor
          </h3>
          <div className="mb-4 p-3 bg-slate-50 rounded-xl border border-slate-200">
            <p className="text-xs text-slate-400 mb-1">Integrity Score</p>
            <p className={`text-2xl font-bold ${proctoring.score >= 80 ? 'text-emerald-600' : proctoring.score >= 50 ? 'text-amber-600' : 'text-red-500'}`}>
              {proctoring.score}%
            </p>
          </div>
          <div className="space-y-2">
            {proctoring.events.length === 0 ? (
              <p className="text-xs text-slate-400">No alerts detected. Monitoring is active.</p>
            ) : (
              proctoring.events.map((evt, i) => (
                <div key={evt.time || `${evt.type}-${i}`} className="flex items-start gap-2 text-xs p-2.5 bg-amber-50 rounded-lg border border-amber-200">
                  <AlertTriangle className="w-3 h-3 text-amber-500 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-amber-700 font-medium capitalize">{evt.type.replace('_', ' ')}</p>
                    <p className="text-amber-500 font-mono">{evt.time}</p>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
